import HyperHTMLElement from 'hyperhtml-element';
import { render as infernoRender, Fragment } from 'inferno';
import cardTableCSS from '../../styles/less/components/cardTable.less';
import cardCSS from '../../styles/less/components/card.less';
import {
	checkboxStylesheet,
	linkStylesheet,
	buttonStylesheet,
	iconStylesheet
} from './stylesheets';
import { cardTableFormatters } from './cardTableFormatters';
import type { CardTableTemplate } from './cardTableFormatters';

interface CardTableHeadT {
	label?: string;
	type?: string;
}

interface CardTableRowT {
	children?: Array<string>;
	[prop: string]: unknown;
}

const cardTableClassName = 'aras-card-table';
const cellClassName = `${cardTableClassName}__cell`;
const cardClassName = 'aras-card';
const columnTypes: Record<string, string> = {
	boolean: `${cellClassName}_shrunk`,
	icon: `${cellClassName}_shrunk ${cellClassName}_with-icon`,
	draggable: `${cellClassName}_shrunk ${cellClassName}_drag-on`
};
const adoptedStyleSheets = [cardTableCSS, cardCSS].map((css) => {
	const stylesheet = new CSSStyleSheet();
	stylesheet.replaceSync(css);

	return stylesheet;
});

class CardTable extends HyperHTMLElement {
	declare shadowRoot: ShadowRoot;
	declare dragOn: boolean;
	state: {
		indexHead: string[];
		indexRows: string[];
		head: Map<string, CardTableHeadT>;
		rows: Map<string, CardTableRowT>;
		selectedRows: string[];
	} = {
		indexHead: [],
		indexRows: [],
		head: new Map(),
		rows: new Map(),
		selectedRows: []
	};

	static get booleanAttributes() {
		return ['drag-on'];
	}

	static extendFormatters(
		formatter: Record<
			string,
			(
				headId: string,
				rowId: string,
				value: unknown,
				control: CardTable
			) => CardTableTemplate
		>
	): void {
		Object.assign(cardTableFormatters, formatter);
	}

	created(): void {
		this.attachShadow({ mode: 'open' });
		this.shadowRoot.adoptedStyleSheets = [
			buttonStylesheet,
			checkboxStylesheet,
			iconStylesheet,
			linkStylesheet,
			...adoptedStyleSheets
		];
		this.addEventListener('applyChange', ((event: CustomEvent) =>
			this.#applyChange(event)) as EventListener);
		this.addEventListener('selectRow', ((event: CustomEvent) =>
			this.#toggleSelection(event)) as EventListener);
	}

	get head(): Map<string, CardTableHeadT> {
		return this.state.head;
	}

	set head(head: Map<string, CardTableHeadT>) {
		const indexHead = [...head.keys()];
		this.setState({ indexHead, head });
	}

	get rows(): Map<string, CardTableRowT> {
		return this.state.rows;
	}

	set rows(rows: Map<string, CardTableRowT>) {
		const children = [...rows.values()].reduce(
			(arr: Array<string>, rowItem) => {
				const children = rowItem.children ?? [];

				return [...arr, ...children];
			},
			[]
		);
		const indexRows = [...rows.keys()].filter(
			(rowKey) => !children.includes(rowKey)
		);
		this.setState({ indexRows, rows });
	}

	attributeChangedCallback(): void {
		this.render();
	}

	getCellType(headId: string): string {
		const { head } = this.state;
		const headCell = head.get(headId);
		const columnType = headCell?.type;
		const cellType = columnType ?? 'text';

		return cellType;
	}

	render(): void {
		const headNode = this.#buildHead();
		const contentNode = this.#buildRows();
		const root = (
			<Fragment>
				{headNode}
				{contentNode}
			</Fragment>
		);
		infernoRender(root, this.shadowRoot);
		this.#checkForScroll();
	}

	#applyChange(event: CustomEvent) {
		const { headId, rowId, newValue } = event.detail;
		const rows = this.state.rows;
		const row = rows.get(rowId);
		rows.set(rowId, { ...row, [headId]: newValue });
		this.setState(rows);
	}

	#buildHead() {
		const { indexHead, indexRows, head } = this.state;
		const headCells = indexHead.map((headId) => {
			const className = this.#getHeadCellClass(headId);
			const headCell = head.get(headId);
			const label = headCell?.label ?? '';
			const cell = (
				<span className={className} role="columnheader">
					{label}
				</span>
			);

			return cell;
		});
		if (this.dragOn) {
			const className = columnTypes.draggable;
			headCells.unshift(<span className={className} role="columnheader" />);
		}

		const rowShrunk = indexRows.some((rowId) => this.#hasChildren(rowId));
		const headClassName = rowShrunk
			? `${cardTableClassName}__head ${cardTableClassName}__row_shrunk`
			: `${cardTableClassName}__head`;
		const headNode = (
			<div className={headClassName} role="row">
				{headCells}
			</div>
		);

		return headNode;
	}

	#buildRows() {
		const { indexHead, indexRows, rows } = this.state;
		const rowShrunk = indexRows.some((rowId) => this.#hasChildren(rowId));
		const draggable = this.dragOn || undefined;
		const rowsNodes = indexRows.map((rowId) => {
			const rowItem = rows.get(rowId);
			const cells = indexHead.map((headId) => {
				const value = rowItem?.[headId];
				const cellTemplate = this.#getCellTemplate(headId, rowId, value);

				return cellTemplate;
			});
			if (this.dragOn) {
				const cellTemplate = this.#getDragTemplate();
				const cell = this.#parseCellTemplate(cellTemplate);
				cells.unshift(cell);
			}

			const color = rowItem?.color;
			const borderStyle = color
				? `border-left: 4px solid ${color};`
				: undefined;
			const classList = this.#getRowClasses(rowId, rowShrunk);
			const rowNodeAttributes = {
				className: classList,
				'data-row-id': rowId,
				draggable,
				role: 'row',
				style: borderStyle
			};
			const expandButton = this.#getExpandButton(rowId);
			const rowNode = expandButton ? (
				<div className={`${cardTableClassName}__row-wrapper`}>
					<div {...rowNodeAttributes}>{cells}</div>
					{expandButton}
				</div>
			) : (
				<div {...rowNodeAttributes}>{cells}</div>
			);

			return rowNode;
		});
		const dragEvents = this.dragOn
			? {
					ondragstart: this.#dragStart,
					ondragover: this.#dragOver,
					ondrop: (event: DragEvent) => this.#drop(event)
			  }
			: {};
		const contentNodeAttributes = {
			className: `${cardTableClassName}__rows`,
			onclick: (event: MouseEvent) => this.#click(event),
			role: 'rowgroup',
			...dragEvents
		};
		const contentNode = <div {...contentNodeAttributes}>{rowsNodes}</div>;

		return contentNode;
	}

	#calculateCursorPosition(event: DragEvent, draggedItemId: string) {
		const { target, clientY } = event;
		if (!(target instanceof HTMLElement)) {
			return '';
		}

		const overItem = target.closest<HTMLElement>('.aras-card');
		if (!overItem) {
			return;
		}

		const draggedItemParent = this.#getParent(draggedItemId);
		const overItemId = overItem.dataset.rowId ?? '';
		const overItemParent = this.#getParent(overItemId);
		const sameParent = draggedItemParent === overItemParent;
		const sameItem = draggedItemId === overItemId;
		if (sameItem || !sameParent) {
			return '';
		}

		const { top: cardTop } = overItem.getBoundingClientRect();
		const topAreaBounding = cardTop + overItem.offsetHeight / 2;
		const above = clientY >= cardTop && clientY < topAreaBounding;
		const position = above ? 'above' : 'bellow';

		return position;
	}

	#checkForScroll(): void {
		const { firstElementChild: headNode, lastElementChild: contentNode } =
			this.shadowRoot;
		if (!(contentNode instanceof HTMLElement)) {
			return;
		}

		const { offsetHeight, scrollHeight } = contentNode;
		const hasScroll = scrollHeight > offsetHeight;
		const className = `${cardTableClassName}__head_scroll-on`;
		headNode?.classList.toggle(className, hasScroll);
	}

	#click(event: MouseEvent): void {
		const { ctrlKey, metaKey, shiftKey, target } = event;
		if (!(target instanceof HTMLElement)) {
			return;
		}

		const row = target.closest<HTMLElement>('.aras-card');
		if (!row) {
			return;
		}

		const rowId = row.dataset.rowId;
		const type = ctrlKey || metaKey ? 'ctrl' : shiftKey ? 'shift' : 'single';
		const detail = { rowId, type };
		const selectRowEvent = new CustomEvent('selectRow', { detail });
		this.dispatchEvent(selectRowEvent);
	}

	#dragStart(event: DragEvent) {
		const { target: draggedItem, dataTransfer } = event;
		if (draggedItem instanceof HTMLElement) {
			const rowId = draggedItem.dataset.rowId;
			rowId && dataTransfer?.setData('id', rowId);
		}
	}

	#dragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
	}

	#drop(event: DragEvent) {
		const dataTransfer = event.dataTransfer;
		const draggedId = dataTransfer?.getData('id') ?? '';
		const cursorPosition = this.#calculateCursorPosition(event, draggedId);
		if (!cursorPosition) {
			return;
		}

		this.#insertOnPosition(event, draggedId, cursorPosition);
	}

	#getButtonState(rowId: string) {
		const expanded = this.#isExpanded(rowId);
		const state = expanded ? 'collapse' : 'expand';

		return state;
	}

	#getHeadCellClass(headId: string): string {
		const { head } = this.state;
		const headCell = head.get(headId);
		const columnType = headCell?.type ?? '';
		const cellClassModifier = columnTypes[columnType];
		const className = cellClassModifier
			? `${cellClassName} ${cellClassModifier}`
			: cellClassName;

		return className;
	}

	#getCellTemplate(headId: string, rowId: string, value: unknown) {
		const cellType = this.getCellType(headId);
		const formatter = cardTableFormatters[cellType] ?? cardTableFormatters.text;
		const cellTemplate = formatter(headId, rowId, value, this);
		const parsedTemplate = this.#parseCellTemplate(cellTemplate);

		return parsedTemplate;
	}

	#getDragTemplate() {
		const className = `${cellClassName}_shrunk ${cellClassName}_drag-on`;
		const node = <span className="aras-icon-drag" />;
		const template = {
			className,
			children: [node]
		};

		return template;
	}

	#getRowClasses(rowId: string, rowShrunk: boolean) {
		const { selectedRows } = this.state;
		const rowClasses = [cardClassName];
		const isRowSelected = selectedRows.includes(rowId);
		if (isRowSelected) {
			rowClasses.push(`${cardClassName}_selected`);
		}

		if (rowShrunk) {
			rowClasses.push(`${cardTableClassName}__row_shrunk`);
		}

		const classList = rowClasses.join(' ');

		return classList;
	}

	#getExpandButton(rowId: string) {
		const hasChildren = this.#hasChildren(rowId);
		if (!hasChildren) {
			return null;
		}

		const buttonClassList = `aras-button aras-button_b ${cardTableClassName}__expand-button`;
		const state = this.#getButtonState(rowId);
		const iconClassList = `aras-icon-${state}`;
		const buttonAttributes = {
			className: `${buttonClassList} ${iconClassList}`,
			onclick: () => this.#toggleExpanding(rowId)
		};

		return <button {...buttonAttributes}></button>;
	}

	#getParent(rowId: string) {
		const data = [...this.rows.values()];
		const parent = data.find((row) => row.children?.includes(rowId));

		return parent;
	}

	#getRowData(rowId: string, indexRows: Array<string>) {
		const rowIndex = indexRows.indexOf(rowId);
		const expanded = this.#isExpanded(rowId);
		const rowItem = this.rows.get(rowId);
		const children = expanded ? rowItem?.children : null;

		return { rowIndex, children };
	}

	#hasChildren(rowId: string) {
		const row = this.rows.get(rowId);
		const children = row?.children ?? [];
		const hasChildren = children.length > 0;

		return hasChildren;
	}

	#isExpanded(rowId: string) {
		const row = this.rows.get(rowId);
		const children = row?.children;
		if (!children) {
			return false;
		}

		const { indexRows } = this.state;
		const expanded = children.every((childId) => indexRows.includes(childId));

		return expanded;
	}

	#insertOnPosition(event: DragEvent, draggedItemId: string, position: string) {
		const target = event.target;
		if (!(target instanceof HTMLElement)) {
			return;
		}

		const overNode = target.closest<HTMLElement>('.aras-card');
		if (!overNode) {
			return;
		}

		const indexRows = [...this.state.indexRows];
		const { children: draggedChildren, rowIndex: draggedIndex } =
			this.#getRowData(draggedItemId, indexRows);
		const splicedCount = draggedChildren && draggedChildren.length + 1;
		const insertedIds = indexRows.splice(draggedIndex, splicedCount ?? 1);
		const overItemId = overNode.dataset.rowId ?? '';
		const { rowIndex: overIndex, children: overChildren } = this.#getRowData(
			overItemId,
			indexRows
		);
		const length = overChildren?.length;
		const shift = position === 'above' ? 0 : length ? length + 1 : 1;
		const changedRows = [
			...indexRows.slice(0, overIndex + shift),
			...insertedIds,
			...indexRows.slice(overIndex + shift)
		];
		this.setState({ indexRows: changedRows });
	}

	#parseCellTemplate(cellTemplate: CardTableTemplate) {
		const role = 'gridcell';
		const isString = typeof cellTemplate === 'string';
		const isObject = typeof cellTemplate === 'object';
		const isVNode = isObject && 'flags' in cellTemplate;
		if (isVNode || isString || !('children' in cellTemplate)) {
			return (
				<div className={cellClassName} role={role}>
					{cellTemplate}
				</div>
			);
		}

		const { className = '', children } = cellTemplate;
		const classList = className
			? `${cellClassName} ${className}`
			: cellClassName;

		return (
			<div className={classList} role={role}>
				{children}
			</div>
		);
	}

	#toggleExpanding(rowId: string) {
		const row = this.rows.get(rowId);
		const children = row?.children ?? [];
		const { indexRows } = this.state;
		const rowIndex = indexRows.indexOf(rowId) + 1;
		const expanded = this.#isExpanded(rowId);
		const changedRows = expanded
			? indexRows.filter((rowId) => !children.includes(rowId))
			: [
					...indexRows.slice(0, rowIndex),
					...children,
					...indexRows.slice(rowIndex)
			  ];
		this.setState({ indexRows: changedRows });
	}

	#toggleSelection(event: CustomEvent) {
		const { rowId, type } = event.detail;
		if (type === 'single') {
			this.setState({ selectedRows: [rowId] });
			return;
		}

		if (type === 'shift') {
			this.#toggleRangeSelection(rowId);
			return;
		}

		const selectedRows = [...this.state.selectedRows];
		const selectedIndex = selectedRows.indexOf(rowId);
		if (selectedIndex !== -1) {
			selectedRows.splice(selectedIndex, 1);
		} else {
			selectedRows.push(rowId);
		}

		this.setState({ selectedRows });
	}

	#toggleRangeSelection(selectedId: string): void {
		const { indexRows, selectedRows } = this.state;
		const lastSelectedId =
			selectedRows[selectedRows.length - 1] ?? indexRows[0];
		const lastSelectedIndex = indexRows.indexOf(lastSelectedId);
		const selectedIndex = indexRows.indexOf(selectedId);
		const leftBound = Math.min(lastSelectedIndex, selectedIndex);
		const rightBound = Math.max(lastSelectedIndex, selectedIndex) + 1;
		const range = indexRows.slice(leftBound, rightBound);
		if (lastSelectedIndex > selectedIndex) {
			range.reverse();
		}

		const unselectedRows = range.filter(
			(rowId) => !selectedRows.includes(rowId)
		);
		if (unselectedRows.length > 0) {
			this.setState({ selectedRows: [...selectedRows, ...unselectedRows] });
			return;
		}

		const selectedRest = selectedRows.filter(
			(selectedRowId) => !range.includes(selectedRowId)
		);
		this.setState({ selectedRows: [...selectedRest, selectedId] });
	}
}

const name = 'aras-card-table';
if (!customElements.get(name)) {
	CardTable.define(name);
}

export default CardTable;
